'use client'

import {useEffect, useRef, useState} from 'react'
import {Button} from '@fitro/customer-ui/Button'
import {MultiSelectDropdown} from '../calendar/CustomerCalendarToolbar'
import {UNASSIGNED_RESOURCE_ID} from '../../../lib/calendar-resource-filter'
import {getClassColorAccentToken} from '../../../lib/classColors'
import {breakpoints, formatInstructorNames} from '../../../lib/utils'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {useTranslation} from '../../../hooks/useTranslation'
import {usePublicWeekSchedule} from '../../../hooks/usePublicWeekSchedule'
import {
    canShiftLocalWeekRangeBackward,
    getLocalWeekRange,
    getLocalWeekRangeFromDateKey,
    shiftLocalWeekRange,
    type LocalWeekRange,
} from './publicEntryDates'
import {
    PublicScheduleEmptyState,
    PublicScheduleEventCard,
    type PublicScheduleAvailabilityStatus,
    type PublicScheduleCardEvent,
} from './PublicScheduleEventCard'
import {PublicScheduleDayPicker} from './PublicScheduleDayPicker'
import {PublicScheduleWeekControls, type PublicScheduleView} from './PublicScheduleWeekControls'
import {addCalendarDays, createDateTimeFormatter, parseInstant} from '@fitro/date-time'
import {publicScheduleStyles} from './publicEntryStyles'
import {resolvePublicAssetUrl} from './publicAssetUrl'

type PublicSchedulePreviewProps = {
    showBookingSummary?: boolean
    events: PublicScheduleEvent[]
    partnerSlug: string
    weekRange: LocalWeekRange
}

type NormalizedEvent = PublicScheduleCardEvent & {
    key: string
    dateKey: string
    end: Date
    start: Date
}

type ScheduleAvailabilityStatus = PublicScheduleAvailabilityStatus
type ScheduleFilterOption = {id: string; label: string; avatar?: {name: string; src: string | null}}

function collectFilterOptions(options: ScheduleFilterOption[], selected: ScheduleFilterOption[]) {
    return Array.from(new Map([...selected, ...options].map(option => [option.id, option])).values())
        .filter(option => option.id !== UNASSIGNED_RESOURCE_ID)
        .sort((left, right) => left.label.localeCompare(right.label))
}

const VIEW_SESSION_KEY = 'fitro_public_schedule_view'

const WEEK_SCROLL_EDGE_SEEN_SESSION_KEY = 'fitro_public_schedule_scroll_edge_seen'

type WeekScrollPosition = 'none' | 'start' | 'middle' | 'end'

function getWeekScrollPosition(element: HTMLDivElement): WeekScrollPosition {
    const maxScrollLeft = element.scrollWidth - element.clientWidth
    if (maxScrollLeft <= 1) return 'none'
    if (element.scrollLeft <= 1) return 'start'
    if (element.scrollLeft >= maxScrollLeft - 1) return 'end'
    return 'middle'
}


function getString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null
}

function isRenderablePublicEvent(event: PublicScheduleEvent): boolean {
    return event.status === undefined
        || event.status === null
        || event.status === 'scheduled'
        || event.status === 'active'
        || event.status === 'completed'
}

function normalizeAvailabilityStatus(value: string): ScheduleAvailabilityStatus {
    if (value === 'last_spots' || value === 'full' || value === 'registration_closed' || value === 'occurred') {
        return value
    }

    return 'available'
}

function buildWeekDays(weekRange: LocalWeekRange) {
    return Array.from({length: 7}, (_, index) => ({key: addCalendarDays(weekRange.startDate, index)}))
}

function normalizeEvents(events: PublicScheduleEvent[], dateTime: ReturnType<typeof createDateTimeFormatter>, fallbacks: {className: string}) {
    const now = Date.now()

    return events
        .filter(isRenderablePublicEvent)
        .map((event): NormalizedEvent | null => {
            const start = parseInstant(event.startDateTime)
            const end = parseInstant(event.endDateTime)
            if (!start || !end) return null
            const eventDateTime = createDateTimeFormatter({locale: dateTime.locale, timeZone: event.timeZone})
            const day = eventDateTime.dateKeyInZone(start)
            if (!day) return null
            const endDay = eventDateTime.dateKeyInZone(end)
            const shortDate = (key: string) => eventDateTime.formatLocalDateWithWeekday(key, {
                dateStyle: 'dayMonth',
                weekday: 'short',
                context: 'inline',
                includeYear: false,
            })

            const className = getString(event.className) ?? fallbacks.className
            const instructor = getString(formatInstructorNames(event.instructors, ''))
            const instructorAvatars = event.instructors.flatMap(item => {
                const src = resolvePublicAssetUrl(item.avatarUrl)
                const name = getString([item.firstName, item.lastName].filter(Boolean).join(' '))
                return name ? [{name, src}] : []
            })
            const location = getString(event.location?.name)

            return {
                id: event.id,
                key: event.id,
                accentColor: getClassColorAccentToken(event.classColorKey, className),
                availabilityStatus: end.getTime() <= now ? 'occurred' : normalizeAvailabilityStatus(event.availabilityStatus),
                className,
                dateLabel: shortDate(day),
                dateKey: day,
                instructor,
                instructorAvatars,
                location,
                sourceEvent: event,
                end,
                start,
                startTimeLabel: eventDateTime.formatInstantTime(start),
                endTimeLabel: eventDateTime.formatInstantTime(end),
                endDateLabel: endDay && endDay !== day ? shortDate(endDay) : null,
            }
        })
        .filter((event): event is NormalizedEvent => Boolean(event))
        .sort((left, right) => {
            const startDifference = left.start.getTime() - right.start.getTime()
            if (startDifference !== 0) return startDifference
            const endDifference = left.end.getTime() - right.end.getTime()
            if (endDifference !== 0) return endDifference
            return left.id.localeCompare(right.id)
        })
}

export function PublicSchedulePreview({events: initialEvents, partnerSlug, weekRange: initialWeekRange, showBookingSummary = true}: PublicSchedulePreviewProps) {
    const {t, dateTime} = useTranslation('landing')
    const {t: tCalendar} = useTranslation('client')
    const {t: tCommon} = useTranslation('common')
    // The week is component state, not a URL parameter, so shared schedule links never pin a week.
    const [weekRange, setWeekRange] = useState(initialWeekRange)
    const weekQuery = usePublicWeekSchedule(partnerSlug, weekRange, {weekStart: initialWeekRange.startDate, events: initialEvents})
    const events = weekQuery.data ?? []
    const [selectedLocations, setSelectedLocations] = useState<ScheduleFilterOption[] | null>(null)
    const [selectedInstructors, setSelectedInstructors] = useState<ScheduleFilterOption[] | null>(null)
    const renderableEvents = events.filter(isRenderablePublicEvent)
    const locationOptions = collectFilterOptions(renderableEvents.flatMap(event => {
        const label = getString(event.location?.name)
        return event.location && label ? [{id: event.location.id, label}] : []
    }), selectedLocations ?? [])
    const instructorOptions = collectFilterOptions(renderableEvents.flatMap(event => event.instructors.flatMap(instructor => {
        const label = getString([instructor.firstName, instructor.lastName].filter(Boolean).join(' '))
        return label ? [{id: instructor.id, label, avatar: {name: label, src: resolvePublicAssetUrl(instructor.avatarUrl)}}] : []
    })), selectedInstructors ?? [])
    if (renderableEvents.some(event => !event.location) || selectedLocations?.some(option => option.id === UNASSIGNED_RESOURCE_ID)) {
        locationOptions.push({id: UNASSIGNED_RESOURCE_ID, label: tCalendar('calendar.filters.withoutLocation')})
    }
    if (renderableEvents.some(event => event.instructors.length === 0) || selectedInstructors?.some(option => option.id === UNASSIGNED_RESOURCE_ID)) {
        instructorOptions.push({id: UNASSIGNED_RESOURCE_ID, label: tCalendar('calendar.filters.withoutInstructor')})
    }
    const locationIds = (selectedLocations ?? locationOptions).map(option => option.id)
    const instructorIds = (selectedInstructors ?? instructorOptions).map(option => option.id)
    const filteredEvents = renderableEvents.filter(event =>
        (selectedLocations === null || locationIds.includes(event.location?.id ?? UNASSIGNED_RESOURCE_ID))
        && (selectedInstructors === null || (event.instructors.length === 0
            ? instructorIds.includes(UNASSIGNED_RESOURCE_ID)
            : event.instructors.some(instructor => instructorIds.includes(instructor.id)))),
    )
    const weekDays = buildWeekDays(weekRange)
    const todayKey = dateTime.today()
    const mobileWeekDays = weekDays
    const compactWeekRange = dateTime.formatLocalDateRange(weekRange.startDate, weekRange.endDate)
    const readableWeekRange = dateTime.formatLocalDateRange(weekRange.startDate, weekRange.endDate, {style: 'long'})
    const normalizedEvents = normalizeEvents(filteredEvents, dateTime, {
        className: t('publicEntry.schedule.fallbackClassName'),
    })
    const canGoPrevious = canShiftLocalWeekRangeBackward(dateTime, weekRange)
    const initialSelectedDateKey = mobileWeekDays.find((day) => day.key === todayKey)?.key ?? mobileWeekDays[0]?.key ?? null
    const [selectedDateKey, setSelectedDateKey] = useState(initialSelectedDateKey)
    // Unknown until mounted: the server renders both views and the viewport picks one, so nothing moves on hydration.
    const [view, setView] = useState<PublicScheduleView | null>(null)
    const explicitViewRef = useRef<PublicScheduleView | null>(null)
    const [weekScrollPosition, setWeekScrollPosition] = useState<WeekScrollPosition>('start')
    const [weekScrollEdgeSeen, setWeekScrollEdgeSeen] = useState(false)
    const weekScrollerRef = useRef<HTMLDivElement>(null)
    const todayColumnRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        setSelectedDateKey((currentDateKey) =>
            currentDateKey
            && currentDateKey >= weekRange.startDate
            && currentDateKey <= weekRange.endDate
                ? currentDateKey
                : initialSelectedDateKey,
        )
    }, [initialSelectedDateKey, weekRange.startDate, weekRange.endDate])

    useEffect(() => {
        try {
            const savedView = window.sessionStorage.getItem(VIEW_SESSION_KEY)
            if (savedView === 'day' || savedView === 'week') explicitViewRef.current = savedView
        } catch {
            // Keep the manual choice in memory when browser storage is unavailable.
        }
        const syncResponsiveView = () => {
            setView(explicitViewRef.current ?? (window.innerWidth < breakpoints.lg ? 'day' : 'week'))
        }
        syncResponsiveView()
        window.addEventListener('resize', syncResponsiveView)
        return () => window.removeEventListener('resize', syncResponsiveView)
    }, [])

    const selectView = (nextView: PublicScheduleView) => {
        explicitViewRef.current = nextView
        setView(nextView)
        try {
            window.sessionStorage.setItem(VIEW_SESSION_KEY, nextView)
        } catch {
            // Resizing still preserves the choice for this mounted calendar.
        }
    }

    useEffect(() => {
        try {
            setWeekScrollEdgeSeen(window.sessionStorage.getItem(WEEK_SCROLL_EDGE_SEEN_SESSION_KEY) === '1')
        } catch {
            setWeekScrollEdgeSeen(false)
        }
    }, [])

    useEffect(() => {
        if (view !== 'week' || !weekScrollerRef.current) return

        const scroller = weekScrollerRef.current
        const centerCurrentDay = () => {
            const todayColumn = todayColumnRef.current
            const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
            scroller.scrollLeft = todayColumn
                ? Math.max(0, Math.min(maxScrollLeft, todayColumn.offsetLeft - (scroller.clientWidth - todayColumn.offsetWidth) / 2))
                : 0
        }
        let wasMobile = window.innerWidth < breakpoints.lg
        const updateForViewport = () => {
            const isMobile = window.innerWidth < breakpoints.lg
            if (isMobile && !wasMobile) centerCurrentDay()
            wasMobile = isMobile
            setWeekScrollPosition(getWeekScrollPosition(scroller))
        }

        // Server HTML snaps the narrow week to today; the snap is released before the effect sets the exact position.
        scroller.style.scrollSnapType = 'none'
        if (wasMobile) centerCurrentDay()
        updateForViewport()
        window.addEventListener('resize', updateForViewport)
        return () => window.removeEventListener('resize', updateForViewport)
    }, [view, weekRange.startDate, weekRange.endDate])

    const updateWeekScrollPosition = (element: HTMLDivElement) => {
        const position = getWeekScrollPosition(element)
        setWeekScrollPosition(position)

        if (position === 'end' && !weekScrollEdgeSeen) {
            setWeekScrollEdgeSeen(true)
            try {
                window.sessionStorage.setItem(WEEK_SCROLL_EDGE_SEEN_SESSION_KEY, '1')
            } catch {
                // The visual hint still softens for this page view when storage is unavailable.
            }
        }
    }

    const visibleWeekShadowOpacity = weekScrollEdgeSeen ? 'opacity-40' : 'opacity-100'

    const selectedEvents = selectedDateKey
        ? normalizedEvents.filter((event) => event.dateKey === selectedDateKey)
        : normalizedEvents

    const goToToday = () => {
        setSelectedDateKey(todayKey)
        if (todayKey < weekRange.startDate || todayKey > weekRange.endDate) {
            setWeekRange(getLocalWeekRange(dateTime))
        } else if (weekScrollerRef.current && todayColumnRef.current) {
            const scroller = weekScrollerRef.current
            const column = todayColumnRef.current
            scroller.scrollTo({left: Math.max(0, column.offsetLeft - (scroller.clientWidth - column.offsetWidth) / 2)})
        }
    }

    return (
        <section id="schedule" data-public-entry-schedule-content>
            <div>
                <div className={publicScheduleStyles.filterToolbar} data-testid="public-schedule-filters">
                    <div className={publicScheduleStyles.filterGrid}>
                        <MultiSelectDropdown
                            label={tCalendar('calendar.filters.location')}
                            allLabel={tCalendar('calendar.filters.allLocations')}
                            noneSelectedLabel={tCalendar('calendar.filters.noneSelected')}
                            selectedCountLabel={count => tCalendar('calendar.filters.selectedTypesCount').replace('{count}', String(count))}
                            emptyLabel={tCalendar('calendar.filters.noLocations')}
                            unavailableLabel={tCalendar('calendar.filters.unavailableLocation')}
                            options={locationOptions}
                            selectedIds={locationIds}
                            onChange={ids => setSelectedLocations(ids.length > 0 && locationOptions.every(option => ids.includes(option.id)) ? null : locationOptions.filter(option => ids.includes(option.id)))}
                            triggerClassName={publicScheduleStyles.filterTrigger}
                        />
                        <MultiSelectDropdown
                            label={tCalendar('calendar.filters.instructor')}
                            allLabel={tCalendar('calendar.filters.allInstructors')}
                            noneSelectedLabel={tCalendar('calendar.filters.noneSelected')}
                            selectedCountLabel={count => tCalendar('calendar.filters.selectedTypesCount').replace('{count}', String(count))}
                            emptyLabel={tCalendar('calendar.filters.noInstructors')}
                            unavailableLabel={tCalendar('calendar.filters.unavailableInstructor')}
                            options={instructorOptions}
                            selectedIds={instructorIds}
                            onChange={ids => setSelectedInstructors(ids.length > 0 && instructorOptions.every(option => ids.includes(option.id)) ? null : instructorOptions.filter(option => ids.includes(option.id)))}
                            triggerClassName={publicScheduleStyles.filterTrigger}
                        />
                    </div>
                    <PublicScheduleWeekControls
                        className={publicScheduleStyles.periodRow}
                        compactOnMobile
                        alwaysShowViewSwitcher
                        canGoPrevious={canGoPrevious}
                        onPrevious={() => setWeekRange(shiftLocalWeekRange(dateTime, weekRange, -1))}
                        onNext={() => setWeekRange(shiftLocalWeekRange(dateTime, weekRange, 1))}
                        onSelectWeek={weekStart => setWeekRange(getLocalWeekRangeFromDateKey(dateTime, weekStart))}
                        compactLabel={compactWeekRange}
                        readableLabel={readableWeekRange}
                        selectedDateKey={selectedDateKey}
                        selectedWeekStart={weekRange.startDate}
                        todayKey={todayKey}
                        view={view ?? 'week'}
                        onSelectDate={setSelectedDateKey}
                        onToday={goToToday}
                        onViewChange={selectView}
                    />
                </div>

                {weekQuery.isError ? (
                    <div role="alert" className={`${publicScheduleStyles.calendarTopSpacing} ${publicScheduleStyles.emptyState}`}>
                        <p className={publicScheduleStyles.emptyTitle}>{tCalendar('common.error')}</p>
                        <Button variant="outline" size="small" className="mt-3" disabled={weekQuery.isFetching}
                            onClick={() => weekQuery.refetch()}>{tCommon('actions.retry')}</Button>
                    </div>
                ) : null}
                <div aria-busy={weekQuery.isFetching} className={weekQuery.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                {weekQuery.data !== undefined && view !== 'week' ? (
                    <div className={view === null ? 'lg:hidden' : undefined}>
                        <>
                            <PublicScheduleDayPicker
                                className={publicScheduleStyles.calendarTopSpacing}
                                days={mobileWeekDays}
                                selectedDateKey={selectedDateKey}
                                todayKey={todayKey}
                                formatFullWeekday={(dateKey) => dateTime.formatLocalDateWeekday(dateKey, {width: 'long', context: 'sentence'})}
                                formatMobileWeekday={(dateKey) => dateTime.formatLocalDateWeekday(dateKey, {width: 'short', context: 'sentence'})}
                                formatDayNumber={dateTime.formatLocalDateDay}
                                formatDayMonth={(dateKey) => dateTime.formatLocalDate(dateKey, {style: 'dayMonth'})}
                                onSelectDay={setSelectedDateKey}
                            />
                            <div className={publicScheduleStyles.calendarTopSpacing}>
                                {selectedEvents.length > 0 ? (
                                    <div className="space-y-3">
                                        {selectedEvents.map((event) => (
                                            <PublicScheduleEventCard showBookingSummary={showBookingSummary} key={event.key} event={event} variant="list" />
                                        ))}
                                    </div>
                                ) : !weekQuery.isPlaceholderData ? (
                                    <PublicScheduleEmptyState scope="day" />
                                ) : null}
                            </div>
                        </>
                    </div>
                ) : null}
                {weekQuery.data !== undefined && view !== 'day' ? (
                    <div className={view === null ? 'max-lg:hidden' : undefined}>
                        <div className={`relative ${publicScheduleStyles.calendarTopSpacing} min-w-0 max-w-full overflow-hidden rounded-lg`}>
                            <div ref={weekScrollerRef} onScroll={(event) => updateWeekScrollPosition(event.currentTarget)} className="overflow-x-auto overscroll-x-contain rounded-lg border border-slate-100 bg-white [scrollbar-width:thin] snap-x snap-mandatory" data-testid="public-schedule-week-scroller">
                                <div className="min-w-[1470px] lg:min-w-full">
                                <div className="grid grid-cols-7 bg-white">
                                    {weekDays.map((day) => {
                                        const isToday = day.key === todayKey
                                        return (
                                            <div ref={isToday ? todayColumnRef : undefined} key={day.key} data-today={isToday ? 'true' : undefined} className={`flex min-h-[76px] items-center gap-3 border-r border-dashed border-[var(--landing-primary-100)] px-4 py-4 last:border-r-0 ${isToday ? 'snap-center bg-[var(--landing-primary-50)] shadow-[inset_0_2px_0_var(--landing-primary-500)]' : ''}`}>
                                                <span className={isToday ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[var(--landing-primary-500)] bg-white text-[14px] font-bold leading-none text-[var(--landing-primary-600)]' : 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[var(--landing-primary-100)] bg-white text-[14px] font-bold leading-none text-[var(--landing-neutral-700)]'}>
                                                    {dateTime.formatLocalDateDay(day.key)}
                                                </span>
                                                <div className="min-w-0 text-left">
                                                    <p className="truncate text-[12px] font-normal capitalize leading-[1.2] text-[var(--landing-neutral-500)]">{dateTime.formatLocalDateMonthTitle(day.key)}</p>
                                                    <p className="truncate text-[14px] font-medium capitalize leading-[1.2] text-[var(--landing-neutral-700)]">{dateTime.formatLocalDateWeekday(day.key, {width: 'long', context: 'sentence'})}</p>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="relative grid min-h-[320px] grid-cols-7 divide-x divide-slate-100 bg-white">
                                    {weekDays.map((day) => {
                                        const dayEvents = normalizedEvents.filter((event) => event.dateKey === day.key)
                                        return (
                                            <div key={day.key} className="space-y-3 p-3">
                                                {dayEvents.map((event) => (
                                                    <PublicScheduleEventCard showBookingSummary={showBookingSummary} key={event.key} event={event} variant="grid" />
                                                ))}
                                            </div>
                                        )
                                    })}
                                    {normalizedEvents.length === 0 && !weekQuery.isPlaceholderData ? (
                                        <div className="absolute inset-0 flex items-center justify-center p-6">
                                            <PublicScheduleEmptyState className="w-full max-w-xl" />
                                        </div>
                                    ) : null}
                                </div>
                                </div>
                            </div>
                            <div aria-hidden="true" data-testid="public-schedule-week-shadow-left" className={`pointer-events-none absolute inset-y-px left-px z-10 w-px shadow-[12px_0_18px_8px_rgba(57,98,198,0.16)] lg:hidden ${weekScrollPosition === 'end' ? visibleWeekShadowOpacity : 'opacity-0'}`} />
                            <div aria-hidden="true" data-testid="public-schedule-week-shadow-right" className={`pointer-events-none absolute inset-y-px right-px z-10 w-px shadow-[-12px_0_18px_8px_rgba(57,98,198,0.16)] lg:hidden ${weekScrollPosition === 'start' || weekScrollPosition === 'middle' ? visibleWeekShadowOpacity : 'opacity-0'}`} />
                        </div>
                    </div>
                ) : null}
                </div>
            </div>
        </section>
    )
}
